import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  AdminRole,
  adminUsersApi,
} from '../../../../api/adminUsersApi';
import {
  colors,
  primaryButtonSx,
  tableCellSx,
  tableHeadCellSx,
  typo,
} from '../../../../theme/tokens';
import { WriteButton, WriteIconButton } from '../../../../components/ui';

interface Props {
  roles: AdminRole[];
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
}

type EditingCell = {
  code: string;
  field: 'role_name' | 'description';
  value: string;
} | null;

const RolesTable: React.FC<Props> = ({ roles, onNotify }) => {
  const queryClient = useQueryClient();
  // Role definitions (config.app_role via /api/admin/rbac) are env-local RBAC —
  // editable on read-only envs. Only is_system roles stay non-editable.
  const [editing, setEditing] = useState<EditingCell>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState({ role_code: '', role_name: '', description: '' });
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing?.code, editing?.field]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateMutation = useMutation({
    mutationFn: (args: { code: string; patch: { role_name?: string; description?: string | null } }) =>
      adminUsersApi.updateRole(args.code, args.patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
      setEditing(null);
    },
    onError: (err: any) => {
      setEditing(null);
      const code = err?.response?.data?.error;
      if (code === 'system_role_immutable')
        onNotify('System roles (default, super-admin) cannot be renamed.', 'error');
      else onNotify('Update failed.', 'error');
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      adminUsersApi.createRole({
        role_code: newForm.role_code.trim(),
        role_name: newForm.role_name.trim(),
        description: newForm.description.trim() || null,
      }),
    onSuccess: () => {
      setNewForm({ role_code: '', role_name: '', description: '' });
      setShowAdd(false);
      onNotify('Role created.', 'success');
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'conflict') onNotify('A role with that code already exists.', 'error');
      else onNotify('Create failed.', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (roleCode: string) => adminUsersApi.removeRole(roleCode),
    onSuccess: () => {
      onNotify('Role deleted.', 'success');
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'has_grants') onNotify('Role has assigned users — reassign them first.', 'error');
      else if (code === 'system_role_immutable')
        onNotify('System roles cannot be deleted.', 'error');
      else onNotify('Delete failed.', 'error');
    },
  });

  const commitEdit = useCallback(() => {
    if (!editing) return;
    const current = roles.find((r) => r.role_code === editing.code);
    if (!current) {
      setEditing(null);
      return;
    }
    const originalVal =
      editing.field === 'role_name' ? current.role_name : current.description ?? '';
    if (editing.value === originalVal) {
      setEditing(null);
      return;
    }
    updateMutation.mutate({
      code: editing.code,
      patch:
        editing.field === 'role_name'
          ? { role_name: editing.value.trim() }
          : { description: editing.value.trim() || null },
    });
  }, [editing, roles, updateMutation]);

  const startEdit = (
    code: string,
    field: 'role_name' | 'description',
    value: string,
    isSystem: boolean,
  ) => {
    if (isSystem) {
      onNotify('System roles cannot be renamed.', 'info');
      return;
    }
    setEditing({ code, field, value });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ ...typo.secondaryText }}>
          Two system roles ship by default: <code>default</code> (read-only) and{' '}
          <code>super-admin</code> (full access). They cannot be renamed or deleted.
        </Typography>
        {!showAdd && (
          <WriteButton ignoreEnvReadOnly
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAdd(true)}
            sx={primaryButtonSx}
          >
            Create role
          </WriteButton>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeadCellSx, width: 220 }}>Code</TableCell>
                <TableCell sx={tableHeadCellSx}>Name</TableCell>
                <TableCell sx={tableHeadCellSx}>Description</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 100, textAlign: 'center' }}>
                  Grants
                </TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 80, textAlign: 'center' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((r) => {
                const isEditingName = editing?.code === r.role_code && editing.field === 'role_name';
                const isEditingDesc =
                  editing?.code === r.role_code && editing.field === 'description';
                return (
                  <TableRow key={r.role_code} hover>
                    <TableCell sx={{ ...tableCellSx, ...typo.mono, color: colors.textPrimary }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {r.is_system && (
                          <Tooltip title="System role — cannot be renamed or deleted">
                            <LockIcon sx={{ fontSize: 14, color: colors.textTertiary }} />
                          </Tooltip>
                        )}
                        {r.role_code}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellSx,
                        cursor: isEditingName ? 'text' : r.is_system ? 'not-allowed' : 'pointer',
                      }}
                      onDoubleClick={() => startEdit(r.role_code, 'role_name', r.role_name, r.is_system)}
                    >
                      {isEditingName ? (
                        <TextField
                          size="small"
                          value={editing.value}
                          inputRef={inputRef}
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                          onBlur={commitEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit();
                            if (e.key === 'Escape') setEditing(null);
                          }}
                          inputProps={{ maxLength: 100 }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 13 }}>{r.role_name}</Typography>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellSx,
                        cursor: isEditingDesc ? 'text' : r.is_system ? 'not-allowed' : 'pointer',
                        color: colors.textSecondary,
                      }}
                      onDoubleClick={() =>
                        startEdit(r.role_code, 'description', r.description ?? '', r.is_system)
                      }
                    >
                      {isEditingDesc ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={editing.value}
                          inputRef={inputRef}
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                          onBlur={commitEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit();
                            if (e.key === 'Escape') setEditing(null);
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                          {r.description || (
                            <span style={{ color: colors.textTertiary, fontStyle: 'italic' }}>
                              {r.is_system ? '—' : 'Double-click to add…'}
                            </span>
                          )}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                        {r.grant_count}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx, textAlign: 'center' }}>
                      <Tooltip
                        title={
                          r.is_system
                            ? 'System roles cannot be deleted'
                            : r.grant_count > 0
                              ? `${r.grant_count} user${r.grant_count === 1 ? '' : 's'} still hold this role`
                              : 'Delete role'
                        }
                      >
                        <span>
                          <WriteIconButton ignoreEnvReadOnly
                            size="small"
                            disabled={r.is_system || r.grant_count > 0 || deleteMutation.isPending}
                            onClick={() => {
                              if (
                                window.confirm(`Delete role ${r.role_code}? This can't be undone.`)
                              ) {
                                deleteMutation.mutate(r.role_code);
                              }
                            }}
                            sx={{ color: colors.errorIcon }}
                          >
                            <DeleteIcon fontSize="small" />
                          </WriteIconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}

              {showAdd && (
                <TableRow sx={{ backgroundColor: colors.adminAccentSubtle }}>
                  <TableCell sx={tableCellSx}>
                    <TextField
                      size="small"
                      placeholder="role-code"
                      value={newForm.role_code}
                      onChange={(e) => setNewForm({ ...newForm, role_code: e.target.value })}
                      inputProps={{ maxLength: 50 }}
                      InputProps={{ sx: { fontFamily: 'Fira Code, monospace', fontSize: 12 } }}
                      autoFocus
                    />
                  </TableCell>
                  <TableCell sx={tableCellSx}>
                    <TextField
                      size="small"
                      placeholder="Display name"
                      value={newForm.role_name}
                      onChange={(e) => setNewForm({ ...newForm, role_name: e.target.value })}
                      inputProps={{ maxLength: 100 }}
                    />
                  </TableCell>
                  <TableCell sx={tableCellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Description"
                      value={newForm.description}
                      onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                    />
                  </TableCell>
                  <TableCell sx={tableCellSx}>—</TableCell>
                  <TableCell sx={{ ...tableCellSx, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Save (Enter)">
                        <span>
                          <WriteIconButton ignoreEnvReadOnly
                            size="small"
                            disabled={
                              !newForm.role_code.trim() ||
                              !newForm.role_name.trim() ||
                              createMutation.isPending
                            }
                            onClick={() => createMutation.mutate()}
                            sx={{ color: colors.successIcon }}
                          >
                            <CheckIcon fontSize="small" />
                          </WriteIconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel (Esc)">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowAdd(false);
                            setNewForm({ role_code: '', role_name: '', description: '' });
                          }}
                          sx={{ color: colors.textSecondary }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {roles.length === 0 && !showAdd && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: '8px' }}>
          No roles yet — system roles should have been seeded by migration.
        </Alert>
      )}
    </Box>
  );
};

export default RolesTable;
